import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTrophy, FaUsers, FaClock, FaPlay, FaSpinner, FaPlus, FaFire, FaStar, FaCrown, FaGem, FaBolt, FaShieldAlt, FaRocket, FaAtom, FaLock } from 'react-icons/fa';
import { 
  getActiveChallenges, 
  createChallengeSession, 
  getUserChallengeSession,
  type ChallengeSession 
} from '../lib/challenges';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabaseClient';

interface ChallengeModeProps {
  songs: Array<{
    id: string;
    creator: string;
    song_cover: string;
    language: string;
    is_free: boolean;
  }>;
  hasAccess: boolean;
  onShowSubscriptionModal: () => void;
  onJoinChallenge: (sessionId: string, songId: string, expiresAt: string, rewardPoints: number) => void;
  onCreateChallenge: (songId: string) => void;
}

export default function ChallengeMode({ songs, hasAccess, onShowSubscriptionModal, onJoinChallenge }: ChallengeModeProps) {
  const [challenges, setChallenges] = useState<ChallengeSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSong, setSelectedSong] = useState<string>('');
  const [userCurrentChallenge, setUserCurrentChallenge] = useState<any>(null);
  const [userParticipatedChallenges, setUserParticipatedChallenges] = useState<Set<string>>(new Set());

  // Auth-aware state
  const [authReady, setAuthReady] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  // -------- Auth bootstrap & subscription --------
  useEffect(() => {
    let mounted = true;

    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;

      setIsAuthed(!!session);
      setUserId(session?.user?.id ?? null);
      setAuthReady(true);

      if (session) {
        // initial load after session ready
        await Promise.all([loadChallenges(), checkUserCurrentChallenge(session.user.id), loadUserParticipatedChallenges(session.user.id)]);
      } else {
        setLoading(false);
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setIsAuthed(!!session);
      setUserId(session?.user?.id ?? null);

      if (session?.user?.id) {
        // refresh after login
        loadChallenges();
        checkUserCurrentChallenge(session.user.id);
        loadUserParticipatedChallenges(session.user.id);
      } else {
        // clear after logout
        setChallenges([]);
        setUserCurrentChallenge(null);
        setUserParticipatedChallenges(new Set());
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  // -------- Helpers --------
  const handleSupabaseError = (err: any, context: string) => {
    console.error(context, err);
    const code = err?.code || err?.status;
    if (code === 401) {
      toast.error('Debes iniciar sesión para continuar.');
    } else if (code === '42501') {
      toast.error('Permiso denegado. Verifica tu cuenta o contacta al administrador.');
    } else {
      toast.error(err?.message || 'Ocurrió un error.');
    }
  };

  const loadChallenges = useCallback(async () => {
    if (!isAuthed) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await getActiveChallenges();
      setChallenges(data);
    } catch (error: any) {
      handleSupabaseError(error, 'Error loading challenges');
    } finally {
      setLoading(false);
    }
  }, [isAuthed]);

  const checkUserCurrentChallenge = useCallback(async (uid?: string) => {
    const id = uid ?? userId;
    if (!isAuthed || !id) return;

    try {
      const currentChallenge = await getUserChallengeSession(id);
      setUserCurrentChallenge(currentChallenge);
    } catch (error: any) {
      handleSupabaseError(error, 'Error checking user challenge');
    }
  }, [isAuthed, userId]);

  const loadUserParticipatedChallenges = useCallback(async (uid?: string) => {
    const id = uid ?? userId;
    if (!isAuthed || !id) return;

    try {
      const { data, error } = await supabase
        .from('stripeen_challenge_participants')
        .select('session_id')
        .eq('user_id', id);

      if (error) throw error;

      const participatedSessions = new Set((data || []).map(p => p.session_id));
      setUserParticipatedChallenges(participatedSessions);
    } catch (error: any) {
      handleSupabaseError(error, 'Error loading user participated challenges');
    }
  }, [isAuthed, userId]);

  // Auto-refresh only when authenticated
  useEffect(() => {
    if (!isAuthed) return;
    const interval = setInterval(() => {
      loadChallenges();
      loadUserParticipatedChallenges();
    }, 30000);
    return () => clearInterval(interval);
  }, [isAuthed, loadChallenges, loadUserParticipatedChallenges]);

  const isUserAlreadyParticipated = (sessionId: string): boolean => {
    return userParticipatedChallenges.has(sessionId);
  };

  const handleJoinChallengeClick = (challenge: ChallengeSession) => {
    if (!isAuthed) {
      toast.error('Inicia sesión para unirte a la batalla.');
      return;
    }

    // Check premium access
    if (!hasAccess && challenge.song && !challenge.song.is_free) {
      onShowSubscriptionModal();
      return;
    }

    // Check expiry
    if (new Date(challenge.expires_at) <= new Date()) {
      toast.error('Este desafío ha expirado');
      return;
    }

    // Check if already participated
    if (isUserAlreadyParticipated(challenge.id)) {
      toast.error('Ya participaste en este desafío');
      return;
    }

    onJoinChallenge(
      challenge.id,
      challenge.song_id,
      challenge.expires_at,
      challenge.reward_points
    );
  };

  const handleCreateChallenge = async () => {
    if (!isAuthed) {
      toast.error('Inicia sesión para crear batallas.');
      return;
    }
    if (!selectedSong) return;

    setCreating(true);
    try {
      // 12 hours = 720 minutes (feel free to make configurable)
      const session = await createChallengeSession(selectedSong, 10, 720, 100);
      toast.success('¡Desafío creado exitosamente!');
      setShowCreateModal(false);
      setSelectedSong('');
      await loadChallenges();

      // Auto-join the created challenge
      onJoinChallenge(session.id, session.song_id, session.expires_at, session.reward_points);
    } catch (error: any) {
      handleSupabaseError(error, 'Error creating challenge');
    } finally {
      setCreating(false);
    }
  };

  const formatTimeLeft = (expiresAt: string) => {
    const now = new Date().getTime();
    const expiry = new Date(expiresAt).getTime();
    const remaining = Math.max(0, expiry - now);

    const minutes = Math.floor(remaining / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // -------- UI --------
  return (
    <div className="relative overflow-hidden">
      {/* Auth gate (prevents 401 spam) */}
      {authReady && !isAuthed && (
        <div className="relative z-10 mb-4 rounded-xl border border-yellow-400/30 bg-yellow-500/10 p-4 text-yellow-100">
          <div className="font-bold mb-1">Debes iniciar sesión</div>
          <div className="text-sm opacity-80">
            Inicia sesión para ver y participar en las batallas.
          </div>
        </div>
      )}

      {/* Mobile-Optimized Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
        <motion.div 
          className="absolute -top-10 -left-10 w-32 h-32 bg-gradient-to-br from-yellow-400/15 to-amber-500/10 rounded-full blur-2xl"
          animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute -bottom-10 -right-10 w-40 h-40 bg-gradient-to-br from-purple-400/15 to-pink-400/10 rounded-full blur-2xl"
          animate={{ scale: [1.1, 1, 1.1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Main Container */}
      <div className="relative z-10 backdrop-blur-xl bg-gradient-to-br from-black/90 via-gray-900/70 to-black/90 rounded-2xl border border-gray-700/50 shadow-2xl overflow-hidden">
        
        {/* Header */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 via-amber-500/15 to-yellow-600/20"></div>
          <div className="relative p-4 sm:p-6 border-b border-gray-700/50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-3">
                <motion.div 
                  className="relative flex-shrink-0"
                  animate={{ rotate: [0, 5, -5, 0], scale: [1, 1.05, 1] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-yellow-400 via-amber-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-xl border border-yellow-300/50">
                    <FaTrophy className="w-6 h-6 sm:w-8 sm:h-8 text-black drop-shadow-lg" />
                  </div>
                  <motion.div
                    className="absolute inset-0 rounded-xl border-2 border-yellow-400/60"
                    animate={{ scale: [1, 1.15, 1], opacity: [0.6, 0.2, 0.6] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  />
                </motion.div>
                
                <div className="min-w-0 flex-1">
                  <motion.h2 
                    className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-white via-yellow-200 to-white bg-clip-text text-transparent"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                  >
                    Modo Desafío Elite
                  </motion.h2>
                  <motion.p 
                    className="text-gray-300 text-sm sm:text-base lg:text-lg font-medium"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  >
                    Compite globalmente
                  </motion.p>
                </div>
              </div>
              
              <motion.button
                onClick={() => setShowCreateModal(true)}
                disabled={!isAuthed}
                className="w-full sm:w-auto relative group p-3 sm:p-4 rounded-xl bg-gradient-to-br from-green-500/80 to-emerald-600/80 border border-green-400/50 hover:border-green-300/70 transition-all duration-300 disabled:opacity-50 shadow-lg hover:shadow-green-500/25 overflow-hidden min-h-[48px]"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-green-400 to-emerald-400 opacity-0 group-hover:opacity-30 group-active:opacity-50 transition-opacity duration-300"></span>
                <span className="relative flex items-center justify-center text-white font-bold text-sm sm:text-base">
                  <FaPlus className="mr-2 w-4 h-4" />
                  Crear Desafío
                </span>
              </motion.button>
            </div>
          </div>
        </div>

        {/* Current Challenge */}
        <AnimatePresence>
          {isAuthed && userCurrentChallenge && (
            <motion.div
              className="relative overflow-hidden"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/15 to-indigo-500/20"></div>
              <div className="relative p-4 sm:p-6 border-b border-gray-700/50">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                  <div className="flex items-center space-x-3">
                    <motion.div
                      className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0"
                      animate={{ rotate: [0, 360], scale: [1, 1.1, 1] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <FaRocket className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </motion.div>
                    <div className="min-w-0">
                      <h3 className="text-white font-bold text-base sm:text-lg">Desafío Activo</h3>
                      <p className="text-white/70 text-sm">En progreso</p>
                    </div>
                  </div>
                  <motion.button
                    onClick={() => onJoinChallenge(
                      userCurrentChallenge.sessionId,
                      userCurrentChallenge.songId,
                      userCurrentChallenge.expiresAt,
                      100
                    )}
                    className="w-full sm:w-auto px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 font-bold shadow-lg text-sm sm:text-base min-h-[48px]"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Continuar Batalla
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Active Challenges */}
        <div className="relative p-3 sm:p-6 lg:p-8">
          <motion.h3 
            className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <FaFire className="mr-2 sm:mr-3 text-orange-400 drop-shadow-lg w-5 h-5 sm:w-6 sm:h-6" />
            </motion.div>
            <span className="text-base sm:text-xl lg:text-2xl">Batallas Épicas ({challenges.length})</span>
          </motion.h3>
          
          {!isAuthed ? (
            <div className="text-center py-8 sm:py-12">
              <p className="text-gray-300 text-base sm:text-lg font-medium">
                Inicia sesión para ver las batallas activas.
              </p>
            </div>
          ) : loading ? (
            <div className="text-center py-8 sm:py-12">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6"
              >
                <div className="w-full h-full rounded-full border-4 border-gray-700 border-t-yellow-400 shadow-lg"></div>
              </motion.div>
              <p className="text-gray-300 text-base sm:text-lg font-medium">Cargando batallas...</p>
            </div>
          ) : challenges.length === 0 ? (
            <motion.div 
              className="text-center py-8 sm:py-12 bg-white/5 rounded-xl sm:rounded-2xl border border-white/10"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.5, type: "spring" }}>
                <FaTrophy className="w-16 h-16 sm:w-20 sm:h-20 text-gray-500 mx-auto mb-4 sm:mb-6" />
              </motion.div>
              <h4 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3">Arena Vacía</h4>
              <p className="text-white/60 mb-4 sm:mb-6 text-base sm:text-lg px-4">¡Sé el primero en crear una batalla!</p>
              <motion.button
                onClick={() => setShowCreateModal(true)}
                disabled={!isAuthed}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-yellow-400 to-amber-500 text-black rounded-xl font-bold hover:from-yellow-500 hover:to-amber-600 transition-all duration-300 shadow-lg min-h-[48px] disabled:opacity-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaPlus className="mr-2 w-4 h-4" />
                Crear Primera Batalla
              </motion.button>
            </motion.div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              <AnimatePresence>
                {challenges.map((challenge, index) => (
                  <motion.div
                    key={challenge.id}
                    className="relative overflow-hidden bg-gradient-to-br from-gray-800/80 via-gray-900/60 to-black/80 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-gray-600/50 hover:border-yellow-400/50 transition-all duration-500 shadow-xl group"
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1, type: "spring", stiffness: 100 }}
                    whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
                  >
                    <div className="absolute inset-0 opacity-5">
                      <div className="absolute top-0 right-0 w-20 h-20 sm:w-32 sm:h-32 bg-gradient-to-bl from-white/20 to-transparent rounded-full blur-xl"></div>
                    </div>
                    
                    <div className="relative p-4 sm:p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="relative flex-shrink-0">
                          <img
                            src={challenge.song?.song_cover || '/default-song-cover.jpg'}
                            alt={challenge.song?.creator || 'Song'}
                            className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg object-cover shadow-lg border-2 border-yellow-400/30"
                          />
                          <motion.div
                            className="absolute -top-1 -right-1 w-4 h-4 sm:w-6 sm:h-6 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg"
                            animate={{ rotate: [0, 360], scale: [1, 1.1, 1] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                          >
                            <FaGem className="w-2 h-2 sm:w-3 sm:h-3 text-black" />
                          </motion.div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-bold text-base sm:text-lg mb-1 truncate">
                            {challenge.song?.creator || 'Canción Misteriosa'}
                          </h4>
                          <div className="flex items-center space-x-2">
                            <span className="px-2 py-1 bg-gradient-to-r from-indigo-500/30 to-purple-500/30 text-indigo-200 text-xs rounded-full border border-indigo-400/30 font-medium">
                              {challenge.song?.language || 'spanish'}
                            </span>
                            <motion.div
                              animate={{ scale: [1, 1.2, 1] }}
                              transition={{ duration: 2, repeat: Infinity }}
                              className="w-2 h-2 bg-green-400 rounded-full shadow-lg"
                            />
                          </div>
                        </div>

                        <motion.div
                          className="flex-shrink-0"
                          animate={{ scale: [1, 1.05, 1], opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        >
                          <div className="px-2 py-1 bg-gradient-to-r from-yellow-400/30 to-amber-500/30 text-yellow-200 rounded-full text-xs font-bold border border-yellow-400/50 flex items-center">
                            <FaCrown className="w-2 h-2 sm:w-3 sm:h-3 mr-1" />
                            ELITE
                          </div>
                        </motion.div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6">
                        <motion.div className="text-center bg-gradient-to-br from-yellow-500/20 to-amber-500/20 backdrop-blur-lg rounded-lg sm:rounded-xl p-2 sm:p-3 border border-yellow-400/30" whileHover={{ scale: 1.02 }}>
                          <motion.div animate={{ rotate: [0, 360], scale: [1, 1.05, 1] }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }}>
                            <FaGem className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 mx-auto mb-1" />
                          </motion.div>
                          <div className="text-lg sm:text-xl font-bold text-yellow-400">{challenge.reward_points}</div>
                          <div className="text-white/60 text-xs font-medium">Puntos</div>
                        </motion.div>
                        
                        <motion.div className="text-center bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur-lg rounded-lg sm:rounded-xl p-2 sm:p-3 border border-blue-400/30" whileHover={{ scale: 1.02 }}>
                          <motion.div animate={{ scale: [1, 1.1, 1], rotate: [0, 10, -10, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
                            <FaUsers className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 mx-auto mb-1" />
                          </motion.div>
                          <div className="text-lg sm:text-xl font-bold text-blue-400">{challenge.participant_count || 0}</div>
                          <div className="text-white/60 text-xs font-medium">Guerreros</div>
                        </motion.div>
                        
                        <motion.div className="text-center bg-gradient-to-br from-red-500/20 to-orange-500/20 backdrop-blur-lg rounded-lg sm:rounded-xl p-2 sm:p-3 border border-red-400/30" whileHover={{ scale: 1.02 }}>
                          <motion.div animate={{ scale: [1, 1.05, 1], opacity: [0.8, 1, 0.8] }} transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}>
                            <FaClock className="w-4 h-4 sm:w-5 sm:h-5 text-red-400 mx-auto mb-1" />
                          </motion.div>
                          <div className="text-lg sm:text-xl font-bold text-red-400">{formatTimeLeft(challenge.expires_at)}</div>
                          <div className="text-white/60 text-xs font-medium">Tiempo</div>
                        </motion.div>
                      </div>

                      <motion.button
                        onClick={() => handleJoinChallengeClick(challenge)}
                        disabled={
                          !isAuthed ||
                          new Date(challenge.expires_at) <= new Date() || 
                          isUserAlreadyParticipated(challenge.id) ||
                          (!hasAccess && challenge.song && !challenge.song.is_free)
                        }
                        className="relative w-full overflow-hidden bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 text-black rounded-lg sm:rounded-xl font-bold text-sm sm:text-base py-3 sm:py-4 hover:from-yellow-500 hover:via-amber-600 hover:to-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-yellow-400/25 group min-h-[48px]"
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                          animate={{ x: ['-100%', '100%'] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        />
                        <span className="relative flex items-center justify-center">
                          {!isAuthed ? (
                            <>
                              <FaLock className="mr-2 w-4 h-4" />
                              Inicia sesión
                            </>
                          ) : new Date(challenge.expires_at) <= new Date() ? (
                            <>
                              <FaClock className="mr-2 w-4 h-4" />
                              Batalla Terminada
                            </>
                          ) : isUserAlreadyParticipated(challenge.id) ? (
                            <>
                              <FaShieldAlt className="mr-2 w-4 h-4" />
                              Ya Participaste
                            </>
                          ) : !hasAccess && challenge.song && !challenge.song.is_free ? (
                            <>
                              <FaLock className="mr-2 w-4 h-4" />
                              Requiere Premium
                            </>
                          ) : (
                            <>
                              <motion.div animate={{ scale: [1, 1.1, 1], rotate: [0, 10, -10, 0] }} transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}>
                                <FaBolt className="mr-2 w-4 h-4" />
                              </motion.div>
                              ¡Entrar a la Batalla!
                            </>
                          )}
                        </span>
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 to-black/60"></div>
          <div className="relative p-4 sm:p-6 border-t border-gray-700/50">
            <div className="text-center">
              <motion.div
                className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-2 mb-2 sm:mb-3"
                animate={{ scale: [1, 1.01, 1] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <FaTrophy className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
                <span className="text-gray-300 font-medium text-sm sm:text-base text-center">
                  Compite y domina la clasificación mundial
                </span>
                <FaBolt className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
              </motion.div>
              
              <div className="flex justify-center space-x-1 sm:space-x-2">
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-yellow-400/60 rounded-full"
                    animate={{ scale: [1, 1.3, 1], opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2, ease: "easeInOut" }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Challenge Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-md border border-gray-600/50 overflow-hidden max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/5 via-transparent to-purple-400/5"></div>
              
              <div className="relative p-4 sm:p-6 border-b border-gray-700/50 bg-gradient-to-r from-yellow-400/10 to-amber-500/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <motion.div
                      className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg flex-shrink-0"
                      animate={{ rotate: [0, 5, -5, 0], scale: [1, 1.05, 1] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <FaTrophy className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
                    </motion.div>
                    <div className="min-w-0">
                      <h3 className="text-lg sm:text-xl font-bold text-white truncate">Crear Batalla</h3>
                      <p className="text-gray-300 text-xs sm:text-sm">Desafía estudiantes</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="p-2 rounded-full hover:bg-white/10 transition-colors text-gray-400 hover:text-white flex-shrink-0 ml-2"
                  >
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="relative p-4 sm:p-6 space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-sm font-bold text-white mb-3">
                    Seleccionar Canción de Batalla
                  </label>
                  <select
                    value={selectedSong}
                    onChange={(e) => setSelectedSong(e.target.value)}
                    className="w-full px-3 sm:px-4 py-3 bg-gray-800/80 backdrop-blur-lg border border-gray-600/50 rounded-lg sm:rounded-xl text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                  >
                    <option value="">Elige tu arma musical...</option>
                    {songs.map((song) => (
                      <option key={song.id} value={song.id}>
                        {song.creator} ({song.language})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bg-gradient-to-br from-yellow-400/10 via-amber-500/5 to-yellow-600/10 border border-yellow-400/30 rounded-lg sm:rounded-xl p-4 sm:p-6 backdrop-blur-lg">
                  <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                    <motion.div animate={{ rotate: [0, 360], scale: [1, 1.05, 1] }} transition={{ duration: 4, repeat: Infinity, ease: "linear" }}>
                      <FaAtom className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
                    </motion.div>
                    <h4 className="font-bold text-yellow-200 text-base sm:text-lg">Configuración</h4>
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
                    <div className="bg-white/5 rounded-lg p-2 sm:p-3 border border-white/10">
                      <div className="text-lg sm:text-xl font-bold text-white mb-1">12</div>
                      <div className="text-white/60 text-xs font-medium">Hrs</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2 sm:p-3 border border-white/10">
                      <div className="text-lg sm:text-xl font-bold text-white mb-1">10</div>
                      <div className="text-white/60 text-xs font-medium">Max</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2 sm:p-3 border border-white/10">
                      <div className="text-lg sm:text-xl font-bold text-white mb-1">100</div>
                      <div className="text-white/60 text-xs font-medium">Pts</div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col space-y-3 pt-2">
                  <motion.button
                    onClick={handleCreateChallenge}
                    disabled={!selectedSong || creating || !isAuthed}
                    className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg sm:rounded-xl text-black font-bold transition-all duration-300 shadow-lg hover:shadow-yellow-400/25 overflow-hidden min-h-[48px] text-sm sm:text-base"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                      animate={{ x: ['-100%', '100%'] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    />
                    <span className="relative flex items-center justify-center">
                      {creating ? (
                        <>
                          <FaSpinner className="animate-spin mr-2 w-4 h-4" />
                          Creando...
                        </>
                      ) : (
                        <>
                          <motion.div animate={{ rotate: [0, 360], scale: [1, 1.05, 1] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
                            <FaRocket className="mr-2 w-4 h-4" />
                          </motion.div>
                          ¡Iniciar Batalla!
                        </>
                      )}
                    </span>
                  </motion.button>
                  
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="w-full px-4 sm:px-6 py-3 bg-gray-700/80 hover:bg-gray-600/80 rounded-lg sm:rounded-xl text-white font-medium transition-all duration-300 backdrop-blur-lg border border-gray-600/50 min-h-[48px] text-sm sm:text-base"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Custom Styles */}
      <style>{`
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-5px); } }
        @keyframes glow { 0%, 100% { box-shadow: 0 0 15px rgba(251, 191, 36, 0.3); } 50% { box-shadow: 0 0 25px rgba(251, 191, 36, 0.5); } }
        .animate-shimmer { background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent); background-size: 200% 100%; animation: shimmer 2s infinite; }
        .animate-float { animation: float 2s ease-in-out infinite; }
        .animate-glow { animation: glow 2s ease-in-out infinite; }

        @media (max-width: 640px) {
          .backdrop-blur-xl { backdrop-filter: blur(8px); }
          button { min-height: 44px; min-width: 44px; }
          .font-bold { font-weight: 600; }
          @media (prefers-reduced-motion: reduce) {
            * { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }
          }
        }

        @media (hover: none) and (pointer: coarse) {
          .group:hover { transform: none; }
          .group:active { transform: scale(0.98); }
        }
      `}</style>
    </div>
  );
}
